/**
 * Industry-specific Custom Instructions for InstantDemoWidget
 * V2.0 - More specific, actionable instructions with sentiment awareness
 *
 * Each industry has:
 * - alwaysInclude: Things to emphasize (with specific examples)
 * - neverSay: Things to avoid (with specific bad phrases)
 * - signaturePhrases: Authentic industry-specific language
 * - forPositive: Extra guidance for positive reviews
 * - forNegative: Extra guidance for negative reviews
 */

export const industryInstructions = {
  // === FOOD & HOSPITALITY ===
  restaurant: {
    alwaysInclude:
      'Mention specific food items by name when referenced. Use words like "fresh", "homemade", "house-made", "scratch-made". Reference the kitchen team or specific staff by name if mentioned.',
    neverSay:
      'Never say "dining experience" or "culinary journey". Never use "we strive to provide". Never mention other restaurants.',
    signaturePhrases: ['back at our table', 'from our kitchen', 'our team in the back'],
    forPositive: 'Reference specific dishes mentioned. Thank them for noticing the details.',
    forNegative:
      'Take ownership of kitchen/service issues. Offer to make it right personally. Never blame suppliers or staff publicly.',
  },
  hotel: {
    alwaysInclude:
      'Reference specific room features or amenities mentioned. Use "your stay" not "your experience". Mention staff by name if referenced.',
    neverSay:
      'Never mention room rates or pricing. Never say "hospitality experience". Never compare to other hotels. Never use "world-class".',
    signaturePhrases: ['your home away from home', 'our doors are always open', 'welcome back anytime'],
    forPositive: 'Highlight what made their stay special. Invite them back for a specific reason.',
    forNegative:
      'Acknowledge the specific issue. Offer direct contact (GM email/phone). Never make excuses about occupancy or staffing.',
  },
  coffeeshop: {
    alwaysInclude:
      'Reference specific drinks by name. Mention roast origins or brewing methods if relevant. Use casual, warm language.',
    neverSay:
      'Never say "coffee experience" or "caffeine fix". Never compare to chain coffee shops. Avoid corporate language.',
    signaturePhrases: ['your usual', 'see you tomorrow', 'our baristas loved making'],
    forPositive: 'Reference their drink choice. Make them feel like a regular.',
    forNegative: 'Acknowledge wait times or drink issues directly. Offer to remake it next time.',
  },

  // === HEALTHCARE ===
  dental: {
    alwaysInclude:
      'Emphasize comfort-focused care and gentle approach. Reference modern technology only if mentioned by patient. Use "Dr. [Name]" format.',
    neverSay:
      'NEVER promise "painless" procedures. Never guarantee outcomes. Never discuss costs. Never say "dental experience".',
    signaturePhrases: ['your comfort is our priority', 'healthy smile', 'our caring team'],
    forPositive: 'Thank them for trusting the practice. Reference specific staff mentioned.',
    forNegative:
      'Take concerns seriously. Offer direct callback from dentist. Never dismiss anxiety or pain concerns.',
  },
  dentist: {
    alwaysInclude:
      'Emphasize comfort-focused care and gentle approach. Reference modern technology only if mentioned by patient. Use "Dr. [Name]" format.',
    neverSay:
      'NEVER promise "painless" procedures. Never guarantee outcomes. Never discuss costs. Never say "dental experience".',
    signaturePhrases: ['your comfort is our priority', 'healthy smile', 'our caring team'],
    forPositive: 'Thank them for trusting the practice. Reference specific staff mentioned.',
    forNegative:
      'Take concerns seriously. Offer direct callback from dentist. Never dismiss anxiety or pain concerns.',
  },
  medical: {
    alwaysInclude:
      'Reference personalized care and thorough approach. Use "Dr. [Name]" format. Acknowledge that health matters are serious.',
    neverSay:
      'NEVER diagnose or offer medical advice. Never promise specific outcomes. Never discuss insurance or costs. Never minimize symptoms.',
    signaturePhrases: ['your health matters to us', 'our medical team', 'personalized care'],
    forPositive: 'Thank them for their trust. Reference the care relationship.',
    forNegative:
      'Take concerns very seriously. Offer direct contact with practice manager. Never be defensive about wait times or care.',
  },
  therapy: {
    alwaysInclude:
      'Emphasize safe, supportive environment. Use warm but professional tone. Acknowledge the courage it takes to seek help.',
    neverSay:
      'NEVER reference specific sessions or issues. Never promise "cure" or specific outcomes. Never discuss other clients. Never be clinical.',
    signaturePhrases: ['your journey', 'safe space', 'here for you'],
    forPositive:
      'Acknowledge their progress without specifics. Thank them for sharing their experience.',
    forNegative:
      'Take feedback seriously. Offer direct conversation. Never be defensive about therapeutic approach.',
  },

  // === BEAUTY & WELLNESS ===
  salon: {
    alwaysInclude:
      'Reference specific services or stylists by name. Use words like "look", "style", "transformation". Be enthusiastic but genuine.',
    neverSay:
      'Never criticize previous styling. Never say "pamper yourself". Never use "hair journey". Avoid overly girly language.',
    signaturePhrases: ['love your new look', 'the chair is ready', 'our stylists loved working with'],
    forPositive: 'Reference the specific service/stylist. Invite them back with a suggestion.',
    forNegative:
      'Take responsibility for results. Offer complimentary fix. Never blame client hair type or previous damage.',
  },
  barber: {
    alwaysInclude:
      'Use straightforward, masculine language. Reference specific cuts or styles. Mention the barber by name if referenced.',
    neverSay:
      'Never say "grooming experience". Never use flowery language. Never criticize previous cuts. Avoid sales-y language.',
    signaturePhrases: ['looking sharp', 'see you in a few weeks', 'the usual'],
    forPositive: 'Keep it simple and confident. Reference the cut style.',
    forNegative: 'Offer to fix it, no questions asked. Be direct and solution-focused.',
  },
  spa: {
    alwaysInclude:
      'Emphasize relaxation and rejuvenation. Reference specific treatments. Use calming, serene language.',
    neverSay:
      'Never make medical claims. Never promise "transformation". Never use "pamper". Avoid clichés like "escape from reality".',
    signaturePhrases: ['your wellness', 'peaceful retreat', 'rejuvenated'],
    forPositive: 'Reference the specific treatment experience. Invite them back for another service.',
    forNegative:
      'Apologize for disrupting their relaxation. Offer to make it right with a return visit.',
  },
  fitness: {
    alwaysInclude:
      'Use motivating, supportive language. Reference specific classes, trainers, or equipment. Celebrate their commitment.',
    neverSay:
      'NEVER guarantee results or body changes. Never use "transform your body". Never be preachy about fitness. Avoid gym-bro language.',
    signaturePhrases: ['keep crushing it', 'proud of your commitment', 'see you at the gym'],
    forPositive: 'Celebrate their dedication. Reference specific achievements or classes.',
    forNegative:
      'Take equipment/cleanliness issues seriously. Offer to address with specific action.',
  },

  // === HOME SERVICES ===
  plumber: {
    alwaysInclude:
      'Emphasize reliability and honest pricing. Reference the specific job done. Use professional but friendly language.',
    neverSay:
      'Never criticize previous plumbers work. Never guarantee exact timing. Never use technical jargon without explanation.',
    signaturePhrases: ['job done right', 'our team takes pride', 'call us anytime'],
    forPositive: 'Reference the specific repair. Thank them for their trust.',
    forNegative:
      'Take responsibility. Offer to return and fix without additional charge. Never blame parts or suppliers.',
  },
  electrician: {
    alwaysInclude:
      'Emphasize safety-first approach and proper licensing. Reference specific work done. Use professional, trustworthy tone.',
    neverSay:
      'Never minimize electrical safety. Never criticize DIY attempts harshly. Never guarantee work timing exactly.',
    signaturePhrases: ['safe and up to code', 'our licensed team', 'power back on'],
    forPositive: 'Reference the specific work. Emphasize the safety aspect.',
    forNegative:
      'Take safety concerns very seriously. Offer immediate follow-up. Never be defensive about safety issues.',
  },
  hvac: {
    alwaysInclude:
      'Reference comfort and efficiency. Mention certified technicians. Use seasonal awareness (heating in winter, cooling in summer).',
    neverSay:
      'Never guarantee exact energy savings percentages. Never criticize other brands harshly. Never use too much technical jargon.',
    signaturePhrases: ['comfortable home', 'our certified techs', 'here when you need us'],
    forPositive: 'Reference the comfort improvement. Thank them for choosing local.',
    forNegative:
      'Take discomfort seriously (its their home!). Offer priority return visit.',
  },
  roofing: {
    alwaysInclude:
      'Emphasize quality materials and experienced crew. Reference specific work done. Use confident, reassuring language.',
    neverSay:
      'Never guarantee exact lifespan. Never criticize previous contractors by name. Never minimize leak concerns.',
    signaturePhrases: ['solid roof over your head', 'our crew takes pride', 'protected for years'],
    forPositive: 'Reference the specific work done. Emphasize the investment in their home.',
    forNegative:
      'Take leak/damage concerns very seriously. Offer immediate inspection. Never blame weather.',
  },
  landscaping: {
    alwaysInclude:
      'Reference specific plants, features, or design elements. Use visual, descriptive language. Emphasize curb appeal.',
    neverSay:
      'Never promise unrealistic growth timelines. Never criticize clients previous landscaping harshly. Avoid overly poetic language.',
    signaturePhrases: ['your outdoor space', 'curb appeal', 'our design came to life'],
    forPositive: 'Reference specific features created. Compliment their vision.',
    forNegative:
      'Take plant health or design concerns seriously. Offer to revisit and adjust.',
  },
  cleaning: {
    alwaysInclude:
      'Emphasize thoroughness and trustworthiness. Reference specific areas cleaned. Use professional, reliable language.',
    neverSay:
      'Never judge clients cleanliness level. Never use "deep clean" excessively. Never discuss what was found.',
    signaturePhrases: ['sparkling clean', 'our trusted team', 'one less thing to worry about'],
    forPositive: 'Reference specific areas they noticed. Thank them for their trust (entering their home).',
    forNegative:
      'Take missed spots seriously. Offer to return and re-clean specific areas free.',
  },
  contractor: {
    alwaysInclude:
      'Reference specific project work. Emphasize craftsmanship and attention to detail. Use confident, professional language.',
    neverSay:
      'Never criticize previous contractors. Never guarantee exact timelines. Never minimize scope changes or delays.',
    signaturePhrases: ['built to last', 'our crew takes pride', 'your vision realized'],
    forPositive: 'Reference the specific project transformation. Thank them for their trust.',
    forNegative:
      'Take timeline/quality concerns seriously. Offer specific remediation plan.',
  },
  homeservices: {
    alwaysInclude:
      'Emphasize reliability and professional service. Reference the specific job. Use trustworthy, friendly language.',
    neverSay:
      'Never criticize previous service providers. Never guarantee exact timing. Never be dismissive of concerns.',
    signaturePhrases: ['job done right', 'our team', 'here when you need us'],
    forPositive: 'Reference the specific service provided. Thank them for choosing local.',
    forNegative: 'Take concerns seriously. Offer concrete solution.',
  },

  // === PROFESSIONAL SERVICES ===
  legal: {
    alwaysInclude:
      'Emphasize client advocacy and experience. Use professional, confident language. Reference specific practice area if mentioned.',
    neverSay:
      'NEVER guarantee case outcomes. Never discuss fees publicly. Never reference specific case details. Never use legal jargon unnecessarily.',
    signaturePhrases: ['fighting for you', 'your advocate', 'our experienced team'],
    forPositive: 'Thank them for their trust during difficult times. Keep it professional.',
    forNegative:
      'Take communication concerns seriously. Offer direct partner contact. Never be defensive.',
  },
  accountant: {
    alwaysInclude:
      'Emphasize attention to detail and financial expertise. Reference tax savings or planning if mentioned. Use confident, trustworthy language.',
    neverSay:
      'Never promise specific tax savings amounts. Never discuss other client details. Never use too much tax jargon.',
    signaturePhrases: ['your financial partner', 'every detail matters', 'peace of mind'],
    forPositive: 'Reference specific value provided (savings, planning). Thank them for long-term trust.',
    forNegative:
      'Take errors very seriously. Offer immediate review and correction.',
  },
  insurance: {
    alwaysInclude:
      'Emphasize personalized coverage and education. Reference specific coverage types if mentioned. Use reassuring language.',
    neverSay:
      'Never guarantee coverage without review. Never criticize other carriers by name. Never oversimplify coverage.',
    signaturePhrases: ['protected when it matters', 'right coverage for you', 'here at claim time'],
    forPositive: 'Reference the specific coverage win. Thank them for their trust.',
    forNegative:
      'Take claim or coverage concerns very seriously. Offer direct agent callback.',
  },
  financial: {
    alwaysInclude:
      'Emphasize fiduciary duty and personalized planning. Reference specific goals if mentioned. Use confident but not boastful language.',
    neverSay:
      'NEVER promise returns or performance. Never guarantee outcomes. Never discuss market predictions. Never use Wall Street jargon.',
    signaturePhrases: ['your financial future', 'planning together', 'fiduciary commitment'],
    forPositive: 'Reference specific planning achievements. Thank them for long-term partnership.',
    forNegative:
      'Take concerns about communication or performance seriously. Offer direct advisor meeting.',
  },
  realestate: {
    alwaysInclude:
      'Reference specific property features or neighborhood. Use "home" not "property" when possible. Emphasize local expertise.',
    neverSay:
      'Never guarantee home values. Never criticize other agents. Never oversell market conditions. Never use "dream home" excessively.',
    signaturePhrases: ['keys in hand', 'perfect fit', 'local expertise'],
    forPositive: 'Reference the specific home/transaction. Celebrate their milestone.',
    forNegative:
      'Take communication or process concerns seriously. Offer direct broker conversation.',
  },

  // === FAMILY SERVICES ===
  daycare: {
    alwaysInclude:
      'Emphasize child safety, learning, and individual attention. Reference specific activities or staff if mentioned. Use warm, caring language.',
    neverSay:
      'NEVER discuss other children. Never promise specific developmental milestones. Never be defensive about safety. Never use corporate childcare speak.',
    signaturePhrases: ['part of our family', 'watching them grow', 'safe and loved'],
    forPositive: 'Reference specific activities or milestones (general). Thank them for their trust with their child.',
    forNegative:
      'Take ANY safety or care concern VERY seriously. Offer immediate director conversation.',
  },
  childcare: {
    alwaysInclude:
      'Emphasize child safety, learning, and individual attention. Reference specific activities or staff if mentioned. Use warm, caring language.',
    neverSay:
      'NEVER discuss other children. Never promise specific developmental milestones. Never be defensive about safety.',
    signaturePhrases: ['part of our family', 'watching them grow', 'safe and loved'],
    forPositive: 'Reference specific activities or milestones (general). Thank them for their trust.',
    forNegative: 'Take ANY concern VERY seriously. Offer immediate director conversation.',
  },
  seniorcare: {
    alwaysInclude:
      'Emphasize dignity, comfort, and compassionate care. Reference specific staff or activities if mentioned. Use respectful, warm language.',
    neverSay:
      'NEVER discuss other residents or their health. Never use clinical language. Never minimize family concerns. Never say "facility".',
    signaturePhrases: ['like family', 'dignity and comfort', 'compassionate care'],
    forPositive: 'Reference specific caregivers or activities. Thank family for trusting with their loved one.',
    forNegative:
      'Take ANY care concern VERY seriously. Offer immediate administrator conversation.',
  },

  // === PET SERVICES ===
  pets: {
    alwaysInclude:
      'Use the pets name if mentioned! Emphasize love for animals. Reference specific services. Use warm, pet-parent language.',
    neverSay:
      'Never criticize pet owners. Never guarantee behavioral outcomes. Never use "just an animal" mindset.',
    signaturePhrases: ['fur baby', 'tail wags', 'our whole team loved'],
    forPositive: 'Use pet name! Reference specific service or personality noted.',
    forNegative:
      'Take concerns about pets care very seriously. Offer owner conversation with specific staff.',
  },
  veterinarian: {
    alwaysInclude:
      'Use pets name if mentioned. Emphasize compassionate care. Reference Dr. by name. Use warm but professional language.',
    neverSay:
      'NEVER promise health outcomes. Never minimize pet parent concerns. Never use overly clinical language for routine visits.',
    signaturePhrases: ['Dr. [Name] and our team', 'healthy and happy', 'part of our family too'],
    forPositive: 'Reference pet by name. Thank them for trusting their fur family member to the practice.',
    forNegative:
      'Take ANY health or care concern very seriously. Offer direct vet callback.',
  },
  grooming: {
    alwaysInclude:
      'Use pets name! Reference specific grooming done (cut, bath, nails). Use enthusiastic, pet-loving language.',
    neverSay:
      'Never criticize coat condition or owner grooming. Never blame pet behavior. Avoid clinical language.',
    signaturePhrases: ['looking fabulous', 'tail was wagging', 'such a good boy/girl'],
    forPositive: 'Use pet name enthusiastically! Reference specific grooming highlights.',
    forNegative:
      'Take concerns seriously. Offer to see pet again and make it right.',
  },

  // === CREATIVE & EVENTS ===
  photographer: {
    alwaysInclude:
      'Reference specific shots or moments if mentioned. Use visual, artistic language. Emphasize capturing memories.',
    neverSay:
      'Never criticize other photographers. Never promise specific shots (weather-dependent). Never oversell editing capabilities.',
    signaturePhrases: ['captured beautifully', 'those moments', 'your story in images'],
    forPositive: 'Reference specific memorable shots. Thank them for trusting their memories to you.',
    forNegative:
      'Take concerns about photos seriously. Offer to review and re-edit if needed.',
  },
  // Alias for backend compatibility (backend uses 'photography', frontend uses 'photographer')
  photography: {
    alwaysInclude:
      'Reference specific shots or moments if mentioned. Use visual, artistic language. Emphasize capturing memories.',
    neverSay:
      'Never criticize other photographers. Never promise specific shots (weather-dependent). Never oversell editing capabilities.',
    signaturePhrases: ['captured beautifully', 'those moments', 'your story in images'],
    forPositive: 'Reference specific memorable shots. Thank them for trusting their memories to you.',
    forNegative:
      'Take concerns about photos seriously. Offer to review and re-edit if needed.',
  },
  wedding: {
    alwaysInclude:
      'Reference specific wedding elements if mentioned. Use celebratory but not over-the-top language. Emphasize the couples vision.',
    neverSay:
      'Never mention other weddings specifically. Never criticize vendor partners. Never use "fairy tale" or "perfect day" excessively.',
    signaturePhrases: ['your special day', 'every detail', 'love story'],
    forPositive: 'Reference specific wedding highlights. Celebrate their milestone.',
    forNegative:
      'Take concerns very seriously (its their wedding!). Offer specific remediation.',
  },
  events: {
    alwaysInclude:
      'Reference specific event elements. Emphasize attention to detail. Use professional, organized language.',
    neverSay:
      'Never criticize venue limitations. Never blame vendors. Never minimize client concerns.',
    signaturePhrases: ['flawless execution', 'every detail', 'your vision'],
    forPositive: 'Reference specific event highlights. Thank them for their trust.',
    forNegative: 'Take concerns seriously. Offer specific follow-up and remediation.',
  },
  creative: {
    alwaysInclude:
      'Reference specific creative elements. Use artistic, collaborative language. Emphasize bringing their vision to life.',
    neverSay:
      'Never criticize client creative choices. Never oversell capabilities. Never dismiss feedback.',
    signaturePhrases: ['your vision', 'creative collaboration', 'brought to life'],
    forPositive: 'Reference specific creative achievements. Thank them for the collaboration.',
    forNegative: 'Take feedback seriously. Offer revisions and direct conversation.',
  },

  // === AUTOMOTIVE ===
  automotive: {
    alwaysInclude:
      'Reference specific repairs or services. Emphasize honest diagnostics. Use straightforward, trustworthy language.',
    neverSay:
      'Never criticize other shops or dealers. Never use fear tactics about car problems. Never guarantee exact costs before diagnosis.',
    signaturePhrases: ['honest diagnosis', 'back on the road', 'our mechanics take pride'],
    forPositive: 'Reference specific repair done. Thank them for trusting the shop.',
    forNegative:
      'Take concerns seriously. Offer to re-inspect and explain repairs.',
  },
  autoshop: {
    alwaysInclude:
      'Reference specific work done. Emphasize certified technicians. Use professional, trustworthy language.',
    neverSay:
      'Never criticize dealer services negatively. Never use scare tactics. Never dismiss concerns about costs.',
    signaturePhrases: ['running right', 'our certified techs', 'honest service'],
    forPositive: 'Reference specific service. Thank them for choosing independent.',
    forNegative: 'Take concerns seriously. Offer manager conversation and re-inspection.',
  },

  // === RETAIL ===
  retail: {
    alwaysInclude:
      'Reference specific products or brands if mentioned. Emphasize product knowledge and helpful recommendations. Use friendly, approachable language.',
    neverSay:
      'Never criticize competing stores. Never make promises about stock availability. Never dismiss product concerns or returns.',
    signaturePhrases: ['our selection', 'our team is happy to help', 'we stand behind our products'],
    forPositive: 'Reference specific products or shopping experience. Thank them for choosing local.',
    forNegative:
      'Take concerns seriously. Offer return/exchange or direct manager contact.',
  },

  // === E-COMMERCE & SOFTWARE ===
  ecommerce: {
    alwaysInclude:
      'Reference specific product if mentioned. Emphasize quality and customer satisfaction. Use friendly, helpful language.',
    neverSay:
      'Never blame shipping carriers directly. Never mention competitor products. Never use overly corporate language.',
    signaturePhrases: ['enjoy your purchase', 'here if you need anything', 'quality guaranteed'],
    forPositive: 'Reference specific product. Invite them to share photos/usage.',
    forNegative:
      'Take shipping or quality concerns seriously. Offer replacement or refund proactively.',
  },
  saas: {
    alwaysInclude:
      'Reference specific features if mentioned. Emphasize ongoing improvements and support. Use helpful, technical-but-accessible language.',
    neverSay:
      'Never criticize competitors directly. Never promise specific feature timelines. Never dismiss user feedback.',
    signaturePhrases: ['our team is on it', 'always improving', 'here to help'],
    forPositive: 'Reference specific features they love. Thank them for being a power user.',
    forNegative:
      'Take feature requests/bugs seriously. Provide specific timeline or workaround if possible.',
  },

  // === EDUCATION ===
  tutor: {
    alwaysInclude:
      'Reference specific subjects or progress. Emphasize personalized approach. Use encouraging, supportive language.',
    neverSay:
      'NEVER guarantee grades. Never criticize school teachers. Never discuss other students.',
    signaturePhrases: ['proud of the progress', 'learning together', 'building confidence'],
    forPositive: 'Reference specific academic achievements (general). Celebrate student effort.',
    forNegative:
      'Take concerns seriously. Offer to adjust approach and discuss directly.',
  },
  education: {
    alwaysInclude:
      'Reference specific programs or achievements. Emphasize growth mindset. Use encouraging, professional language.',
    neverSay:
      'NEVER guarantee outcomes. Never compare to other students. Never be dismissive of concerns.',
    signaturePhrases: ['learning journey', 'growth mindset', 'bright future'],
    forPositive: 'Reference specific achievements. Thank them for being part of the community.',
    forNegative:
      'Take educational concerns seriously. Offer direct conversation with leadership.',
  },

  // === TRAVEL & LEISURE ===
  travel: {
    alwaysInclude:
      'Reference specific destinations or experiences. Use exciting, experiential language. Emphasize personalized itineraries.',
    neverSay:
      'Never criticize destinations. Never guarantee weather. Never oversell experiences.',
    signaturePhrases: ['adventure awaits', 'memories made', 'your perfect trip'],
    forPositive: 'Reference specific trip highlights. Thank them for traveling with you.',
    forNegative:
      'Take trip issues seriously. Offer remediation or credit for future travel.',
  },
  airbnb: {
    alwaysInclude:
      'Reference specific property features. Emphasize local hospitality. Use warm, welcoming language.',
    neverSay:
      'Never compare to hotels negatively. Never mention other guests. Never be defensive about house rules.',
    signaturePhrases: ['your home away from home', 'local tips', 'welcome back anytime'],
    forPositive: 'Reference specific features they enjoyed. Invite them back.',
    forNegative:
      'Take cleanliness or amenity issues seriously. Offer partial refund or future discount.',
  },

  // === DEFAULT / GENERIC ===
  generic: {
    alwaysInclude:
      'Reference specific details from the review. Emphasize what makes the service unique. Use genuine, human language.',
    neverSay:
      'Never use corporate speak. Never say "we strive" or "we appreciate your feedback". Never be generic.',
    signaturePhrases: ['our team', 'here for you', 'thanks for choosing us'],
    forPositive: 'Reference specific details. Thank them genuinely.',
    forNegative: 'Take concerns seriously. Offer specific follow-up.',
  },
};

/**
 * Get custom instructions for a given business type
 * @param {string} businessType - The business type key
 * @param {string} sentiment - 'positive', 'negative', or 'neutral' (optional)
 * @returns {Object} - Full instruction object
 */
export const getIndustryInstructions = (businessType, sentiment = null) => {
  const type = (businessType || 'generic').toLowerCase();
  const instructions = industryInstructions[type] || industryInstructions.generic;

  // Add sentiment-specific instructions if provided
  if (sentiment === 'positive' && instructions.forPositive) {
    return {
      ...instructions,
      alwaysInclude: `${instructions.alwaysInclude} FOR POSITIVE REVIEWS: ${instructions.forPositive}`,
    };
  }
  if (sentiment === 'negative' && instructions.forNegative) {
    return {
      ...instructions,
      alwaysInclude: `${instructions.alwaysInclude} FOR NEGATIVE REVIEWS: ${instructions.forNegative}`,
    };
  }

  return instructions;
};

/**
 * Format custom instructions for API submission
 * Now includes signature phrases for more authentic responses
 * @param {string} alwaysInclude
 * @param {string} neverSay
 * @param {string[]} signaturePhrases (optional)
 * @returns {string|null}
 */
export const formatCustomInstructions = (alwaysInclude, neverSay, signaturePhrases = []) => {
  if (!alwaysInclude && !neverSay) return null;

  let formatted = '';
  if (alwaysInclude) {
    formatted += `ALWAYS DO: ${alwaysInclude}\n`;
  }
  if (neverSay) {
    formatted += `NEVER DO: ${neverSay}\n`;
  }
  if (signaturePhrases && signaturePhrases.length > 0) {
    formatted += `AUTHENTIC PHRASES TO USE: "${signaturePhrases.join('", "')}"`;
  }
  return formatted.trim();
};

export default industryInstructions;
